import { Avatar } from '@app/components/core/avatar/Avatar'
import { TextLink } from '@app/components/core/link/TextLink'
import { Text } from '@app/components/core/typography/Text'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { pages } from '@app/util/links'
import type { Property } from 'csstype'
import { Style } from 'inlines'
import { memo, ReactNode, useCallback } from 'react'
import { useLocation } from 'wouter'

export type UserListProps = {
  users: Omit<UserListItemProps, 'index'>[]
  gap?: Property.Gap<(string & {}) | 0>
  style?: Style
}

export type UserListItemProps = {
  id: number
  name: string
  description?: string
  avatar?: string | { src: string }
  index: number
  handle?: string
  action?: ReactNode
}

export const UserList = ({ users, gap = '1rem', style }: UserListProps) => {
  return (
    <Column
      gap={gap}
      alignItems="stretch"
      style={{ flex: '1', overflowY: 'auto', ...style }}
    >
      {users.map((user, index) => (
        <UserListItem key={user.id} {...user} index={index} />
      ))}
    </Column>
  )
}

const avatarVariants: ('pink' | 'yellow' | 'dimmed-red')[] = [
  'pink',
  'dimmed-red',
  'yellow',
  'pink',
  'yellow',
  'dimmed-red',
]

const UserListItem = memo(
  ({
    id,
    handle,
    avatar,
    name,
    index,
    action,
    description,
  }: UserListItemProps) => {
    const [_, navigate] = useLocation()

    const onAvatarClick = useCallback(() => {
      if (!handle) {
        return
      }
      navigate(pages.profile(handle))
    }, [navigate, handle])

    return (
      <Row
        key={id}
        style={{ justifyContent: 'space-between', alignItems: 'center' }}
      >
        <Row style={{ alignItems: 'center' }}>
          <Avatar
            size="small"
            image={avatar}
            labelSource={name}
            variant={avatarVariants[index % avatarVariants.length]}
            onClick={handle ? onAvatarClick : undefined}
          />
          <Column gap={0} alignItems="stretch">
            {handle && (
              <TextLink isBold={!!description} to={pages.profile(handle)} containerStyle={{ textDecoration: 'none' }}>
                {name}
              </TextLink>
            )}
            {!handle && (
              <Text.Medium isBold={!!description}>{name}</Text.Medium>
            )}
            {description && (
              <Text.Small color="var(--palette-blue-300)">
                {description}
              </Text.Small>
            )}
          </Column>
        </Row>
        {action}
      </Row>
    )
  },
)
